import { SlideDomainEnum, SlideTypeEnum } from "@/generated/graphql";
import type { KnowledgeAreaBySlide, SlideType } from "@ai-teach/types";

// ########### SLIDE DISABLED ELEMENTS RULES ##############

type SlideKnowledgeSetting = {
  [K in SlideType]: readonly KnowledgeAreaBySlide<K>[];
};

// Draggables are disabled here
export const nonDraggablesTypeSlide: SlideKnowledgeSetting = {
  word: ["pronunciation", "usage"],
  grammar: ["form", "meaning", "usage", "sentence"],
  reading: ["fluency", "comprehension"],
  phonics: ["encode", "decode"],
  other: [],
};

// Audio is disabled here
export const nonAudioTypeSlide: SlideKnowledgeSetting = {
  word: [],
  grammar: ["form", "meaning", "usage"],
  reading: [],
  phonics: [],
  other: [],
};

// Video is disabled here
export const nonVideoTypeSlide: SlideKnowledgeSetting = {
  word: ["pronunciation", "usage"],
  grammar: ["form", "meaning", "usage", "sentence"],
  // Both reading areas resolve to passageReading, whose passages take a video as their visual.
  reading: [],
  phonics: ["encode", "decode"],
  other: [],
};

export const nonImageTypeSlide: SlideKnowledgeSetting = {
  word: [],
  grammar: ["form", "meaning", "usage"],
  reading: [],
  phonics: ["encode", "decode"],
  other: [],
};

// ##################################################

export const CANVAS_SIZE = {
  freeform: {
    size: {
      width: 480,
      height: 800,
    },
    aspectRatio: 9 / 15,
    aspectRatioClass: "aspect-[9/15]",
  },
  editor: {
    size: {
      width: 800,
      height: 800,
    },
    aspectRatio: 1 / 1,
    aspectRatioClass: "aspect-square",
  },
} as const;

export const FREEFORM_CANVAS_SLIDE = {
  word: ["meaning"],
  reading: [],
  grammar: [],
  phonics: [],
  other: ["reading", "writing", "listening", "speaking"],
};

export const isFreeformCanvas = (slideType: SlideType, knowledgeArea: string) => {
  return (FREEFORM_CANVAS_SLIDE[slideType] as readonly string[]).includes(knowledgeArea);
};

// ##########################################################################

// Available slide types and knowledge areas ( NOT COMING SOON & READY TO USE )
export const KNOWLEDGE_AREAS = {
  word: ["pronunciation", "vocabulary"], // ["pronunciation", "usage", "meaning","vocabulary"] ❌ ["meaning", "usage"] 不用這些
  grammar: ["form", "response"], // ["form", "meaning", "usage","sentence","response"]] ❌ ["meaning", "usage","sentence"] 不用這些
  reading: ["fluency"], // ["fluency", "comprehension"]
  phonics: ["encode", "decode"],
  other: ["reading"],
} as const;

export const SLIDE_TYPES = [
  { value: "word", label: "Word", enum: SlideTypeEnum.Word },
  { value: "grammar", label: "Grammar", enum: SlideTypeEnum.Grammar },
  { value: "reading", label: "Reading", enum: SlideTypeEnum.Reading },
  { value: "phonics", label: "Phonics", enum: SlideTypeEnum.Phonics },
  { value: "other", label: "Other", enum: SlideTypeEnum.Other },
] as const;

// Skill-first slide creation shows every knowledge area in one flat list (no longer
// gated by slide type). Derived from KNOWLEDGE_AREAS so there is a single source.
//
export const ALL_KNOWLEDGE_AREAS = Object.values(KNOWLEDGE_AREAS).flat();

// `type` is deprecated but still drives slideData defaults on the backend, so we
// derive it from the chosen knowledge area (each area maps to exactly one type).
export const KNOWLEDGE_AREA_TO_TYPE: Record<string, SlideType> = Object.fromEntries(
  Object.entries(KNOWLEDGE_AREAS).flatMap(([type, areas]) =>
    areas.map((area) => [area, type as SlideType])
  )
);

// The dimensions a slide's score is credited to. Independent of the editor's tags —
// a slide tagged one way can report under another.
export const SLIDE_DOMAINS = [
  { label: "Speaking", enum: SlideDomainEnum.Speaking },
  { label: "Listening", enum: SlideDomainEnum.Listening },
  { label: "Reading", enum: SlideDomainEnum.Reading },
  { label: "Grammar", enum: SlideDomainEnum.Grammar },
  { label: "Writing", enum: SlideDomainEnum.Writing },
  { label: "Word Recognition", enum: SlideDomainEnum.WordRecognition },
] as const;

export const AUDIO_SPEED_LIMIT = { min: 70, max: 150, default: 80 } as const;
export const AUDIO_PITCH_LIMIT = { min: -8, max: 8, default: 0 } as const;

// ##########################################################################
